/**
 * #672 Check again: the owner's one control for a tool parked out of usage.
 *
 * An out-of-usage report with no return time never lapses, and only a successful real run
 * clears it - which a parked tool never gets. This asks that computer to run exactly that tool
 * once, reply-only, through the existing owner-AAL2 `enqueue_ai_connection_action`
 * (`recheck_runtime`). The computer reports what the run proved: success clears the report,
 * exhaustion keeps it with a fresh time. Nothing here edits or hides the report; the list
 * changes only when Brunel's rows do.
 */
import { isCurrent, type RuntimeOutageLike } from './outages';

/** Only a current out-of-usage report can be checked again; the others need the owner. */
export function canCheckAgain(row: RuntimeOutageLike, nowMs: number): boolean {
  return row.reason_code === 'out_of_usage' && isCurrent(row, nowMs);
}

/** The owner-facing sentence for a refusal Brunel answered with a code and no further words. */
export function recheckMessage(code: string): string {
  switch (code) {
    case 'not_parked': return 'That tool is no longer parked out of usage on that computer. Refresh to see its current state.';
    case 'forbidden': return 'Only the owner can ask for a check, after confirming it is you (two-step verification).';
    case 'unauthorized': return 'Your session has expired. Sign in again to ask for a check.';
    case 'not_found': return 'Brunel has no such computer any more. Refresh and try again.';
    case 'invalid_input': return 'This tool could not be identified. Refresh and try again.';
    default: return 'The check was not requested.';
  }
}

/** One computer's answer to a queued Check again, as the instruction row already stores it. */
export interface RecheckInstructionState {
  readonly node_id: string;
  readonly runtime_key?: string | null;
  readonly adapter_key?: string | null;
  readonly issued_at: string;
  readonly outcome: string | null;
  readonly detail: string | null;
}

/**
 * The latest refusal that computer wrote for this tool, or null when it has not refused
 * or left the reason blank. A newer answer that is not a refusal clears it. Nothing here
 * is written when the row has no reason of its own.
 */
export function recheckRefusalReason(
  rows: readonly RecheckInstructionState[],
  nodeId: string,
  runtimeKey: string,
): string | null {
  const matches = rows.filter((row) => {
    const key = row.runtime_key ?? row.adapter_key ?? '';
    return row.node_id === nodeId && key === runtimeKey;
  });
  const latest = matches.reduce<RecheckInstructionState | null>((best, row) => {
    if (best === null || row.issued_at > best.issued_at) return row;
    return best;
  }, null);
  if (latest === null || latest.outcome !== 'refused') return null;
  const detail = latest.detail?.trim() ?? '';
  return detail === '' ? null : detail;
}

type RecheckAnswer = {
  ok?: boolean;
  error?: { code?: string; message?: string; detail?: string };
  value?: { alreadyWaiting?: boolean; outcome?: string; detail?: string; reason?: string };
} | null;

/** A sentence the response already carried, ignoring a message that only repeats the error code. */
function statedReason(answer: RecheckAnswer): string | null {
  if (answer === null) return null;
  const code = answer.error?.code?.trim() ?? '';
  const candidates = [answer.error?.detail, answer.value?.detail, answer.value?.reason, answer.error?.message];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const text = candidate.trim();
    if (text === '' || text === code) continue;
    return text;
  }
  return null;
}

export interface RecheckResult {
  readonly ok: boolean;
  readonly message: string;
  /** Set only when the canonical answer said a check was already waiting. */
  readonly alreadyWaiting?: boolean;
}

type RpcAnswer = RecheckAnswer;

/**
 * Send exactly `{orgId, nodeId, action: 'recheck_runtime', runtimeKey}` once, behind the
 * owner's AAL2 guard. It only queues the check; the result arrives as the computer's own
 * report. The request is never replayed, and every outcome ends with the room re-reading.
 */
export async function requestRuntimeRecheck(input: {
  readonly row: RuntimeOutageLike & { readonly org_id: string };
  readonly guard: (name: 'post_owner_command', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'enqueue_ai_connection_action', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
  readonly refresh: () => Promise<void>;
  readonly onResult: (result: RecheckResult) => void;
}): Promise<void> {
  // enqueue_ai_connection_action requires owner aal2; the guard asks for step-up first when needed.
  await input.guard('post_owner_command', async () => {
    const { row } = input;
    let answer: RpcAnswer = null;
    let failed = false;
    try {
      const { data, error } = await input.rpc('enqueue_ai_connection_action', {
        request: { orgId: row.org_id, nodeId: row.node_id, action: 'recheck_runtime', runtimeKey: row.runtime_key },
      });
      answer = data as RpcAnswer;
      failed = Boolean(error);
    } catch {
      failed = true;
    }
    if (!failed && answer?.ok === true && answer.value?.outcome === 'refused') {
      input.onResult({ ok: false, message: statedReason(answer) ?? '' });
    } else if (!failed && answer?.ok === true) {
      const alreadyWaiting = answer.value?.alreadyWaiting === true;
      input.onResult({
        ok: true,
        ...(alreadyWaiting ? { alreadyWaiting: true } : {}),
        message: alreadyWaiting
          ? `A check of ${row.runtime_key} is already waiting on ${row.name}.`
          : `Check requested on ${row.name}. ${row.runtime_key} runs once; the report clears only if it answers.`,
      });
    } else if (!failed && answer?.ok === false) {
      input.onResult({ ok: false, message: statedReason(answer) ?? recheckMessage(answer.error?.code ?? '') });
    } else {
      input.onResult({ ok: false, message: 'Brunel did not confirm the check. Refresh before asking again.' });
    }
    await input.refresh();
  });
}
